<script setup lang="ts">
import {
  NamedBindings,
  getContainer,
  UserServiceInterface,
  TranslationService,
  CaseServiceInterface,
  ValuesSetValue,
  ConsoleLogger,
} from '@dandelion/core';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import leftArrowUrl from '../assets/icon/32/arrow-left.svg';
import { PdfData } from '../entities/PdfData';
import { PdfService } from '../services/PdfService';
import { GenerateHTMLForPrint } from '../utils/GenerateHTMLForPrint';
import { PdfDatasForSave } from '../entities/PdfDatasForPage';
import { NamedServices } from '../enum/NamedServices';
import { computed } from 'vue';

const container = getContainer();
const userService = container.get(
  NamedBindings.UserService
) as UserServiceInterface;
const pdfService = container.get(NamedServices.PdfService) as PdfService;
const caseService = container.get(
  NamedBindings.CaseService
) as CaseServiceInterface;
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;
const logger = container.get(NamedBindings.Logger) as ConsoleLogger;

const route = useRoute();
const caseID = route.params.caseId as string;
const centreCode = userService.getCurrentUser().value.centre;
let casePdfs: Array<PdfData> = [];
await updatePdfs();
const caseData = await caseService.getDataFor(caseID);
const model = caseData.data;

function formatDate(dateString: string): string {
  if (!dateString) return '';
  const parts = dateString.split('-'); // Supponiamo che la data sia in formato "aaaa-mm-gg"
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`; // Converte in "gg/mm/aaaa"
  }
  return dateString; // Se il formato non è quello atteso, restituisce la stringa originale
}

let pdfDatasToBeSaved: PdfDatasForSave = {
  ehrIdAdmission: model.core.administrativeInfo.ehrIdAdmission,
  lastName: model.core.personalData.surname,
  firstName: model.core.personalData.name,
  placeOfBirth: {
    town: model.core.personalData.placeOfBirth.town,
    province: model.core.personalData.placeOfBirth.province,
    country: ts.t(processValue(model.core.personalData.placeOfBirth.country)),
  },
  birthDate: formatDate(model.core.personalData.birthDate),
  address: {
    street: model.core.personalData.address.street,
    postalCode: model.core.personalData.address.postalCode,
    town: model.core.personalData.address.town,
    province: model.core.personalData.address.province,
    country: ts.t(processValue(model.core.personalData.address.country)),
  },
  icuAdmDate: formatDate(model.core.administrativeInfo.icuAdmDate),
  icuAdmTime: model.core.administrativeInfo.icuAdmTime,
  icuDisDate: formatDate(model.core.outcome.icuDisDate),
  icuDisTime: model.core.outcome.icuDisTime,
  sourceOfAdmission: ts.t(processValue(model.core.sourceOfAdmission.admSource)),
  typeOfAdmSource:model.core.sourceOfAdmission.admWard
  ? ts.t(processValue(model.core.sourceOfAdmission.admWard))
  : '--',
  reasonForAdmission:ts.t(processValue(model.core.reasonForAdmission.admReas)),
  clinicCondAdm: model.core.clinicalConditionsOnAdmission.clinicalCondAdm,
  comorbidities: ts.t(processValue(model.core.comorbiditiesPage.comorbidities)),
  allergies: model.core.clinicalCourseNotes.allergie,
  anamnesiFarmly: model.core.clinicalCourseNotes.anamnesiFarmly,
  anamnesiPhy: model.core.clinicalCourseNotes.anamnesiPhy,
  anamnesiRemota: model.core.clinicalCourseNotes.anamnesiRemota,
  anamnesi: model.core.clinicalCourseNotes.anamnesi,
  anamnesiFarm: model.core.clinicalCourseNotes.anamnesiFarm,
  icuDiseases: model.core.complicationsOccurringDuringTheStay.icuDiseases,
  antibiotici: model.core.clinicalCourseNotes.antibiotici,
  ventilationOnAdmission: model.core.ventilationOnAdmission,
  invasiveVentilationOnAdmission: model.core.invasiveVentilationOnAdmission,
  surgicalProcedureAtAdmission: model.core.surgicalProcedureAtAdmission,
  nonSurgicalList: model.core.nonSurgicalInterventions.nonSurgicalList,
  proceduresTreatments: model.core.proceduresTreatments.proceduresList,
  surgicalList: model.core.surgicalInterventions.surgicalList,
  procOrganTranspList: model.core.surgicalInterventions.procOrganTranspList,
  congHeartValSurgYesNo: model.core.surgicalInterventions.congHeartValSurgYesNo,
  decorsoClinico: model.core.clinicalCourseNotes.decorsoClinico,
  terapia: model.core.clinicalCourseNotes.terapia,
};

function processValue(value: any): string {
  if (value === '') {
    return '--';
  }
  if (value instanceof ValuesSetValue) {
    return `<span class="value">${ts.t(value.getLabel())}</span>`;
  } else if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item instanceof ValuesSetValue) {
          return `<span class="value">${ts.t(item.getLabel())}</span>`;
        } else if (typeof item === 'object' && item !== null) {
          return `<span>${processObject(item)}</span>`;
        } else {
          return `<span class="value">${ts.t(String(item))}</span>`;
        }
      })
      .join(', ');
  } else if (typeof value === 'object' && value !== null) {
    return processObject(value);
  } else {
    return `<span class="value">${ts.t(String(value))}</span>`;
  }
}

function processObject(obj: any): string {
  return Object.keys(obj)
    .filter((key) => key !== 'guid')
    .map((key) => {
      const attr = obj[key];
      if (Array.isArray(attr)) {
        return attr
          .map(
            (item) =>
              `<span class="isArray">${key}: ${ts.t(processValue(item))}</span>`
          )
          .join('');
      } else {
        return `<span class="attribute">${key}: ${ts.t(processValue(attr))}</span>`;
      }
    })
    .join('');
}

const logoPath = ref<string>(''); // Inizialmente stringa vuota
const footerPath = ref<string>('');

onMounted(async () => {
  // Attendi il completamento della funzione getLogoPath e assegna il risultato
  logoPath.value = await getLogoPath(centreCode);
  footerPath.value = await getFooterPath(centreCode);
});

async function getLogoPath(centerCode: string): Promise<string> {
  const basePath = '/files/template_image_print/';
  const centerLogoPath = `${basePath}${centerCode}/logo.png`;

  try {
    // Tenta di caricare l'immagine specifica per il centro
    const response = await fetch(centerLogoPath);

    if (response.ok) {
      // Se la risposta è OK, ritorna il percorso del logo
      return centerLogoPath;
    } else {
      // Se il file non esiste, ritorna una stringa vuota
      return '';
    }
  } catch (error) {
    // In caso di errore (es. problemi di rete), ritorna una stringa vuota
    console.error('Errore nel caricamento del logo:', error);
    return '';
  }
}

async function getFooterPath(centerCode: string): Promise<string> {
  const basePath = '/files/template_image_print/';
  const centerFooterPath = `${basePath}${centerCode}/footer.png`;

  try {
    // Tenta di caricare l'immagine specifica per il centro
    const response = await fetch(centerFooterPath);

    if (response.ok) {
      // Se la risposta è OK, ritorna il percorso del logo
      return centerFooterPath;
    } else {
      // Se il file non esiste, ritorna una stringa vuota
      return '';
    }
  } catch (error) {
    // In caso di errore (es. problemi di rete), ritorna una stringa vuota
    console.error('Errore nel caricamento del logo:', error);
    return '';
  }
}


const handleImageError = () => {
  if (logoPath.value) {
    console.error('Immagine non trovata, caricamento fallito!');
    logoPath.value = ''; // Imposta il valore come stringa vuota
  }
};

const handleFooterImageError = () => {
  if (footerPath.value) {
    console.error("Immagine non trovata, caricamento fallito!");
    footerPath.value = ''; // Imposta il valore come stringa vuota
  }
};

const formattedNonSurgicalList = computed(() => {
  const list = pdfDatasToBeSaved.nonSurgicalList;
  if (!Array.isArray(list) || list.length === 0) return ts.t('--');
  return (
    `<ul>` +
    list
      .map(
        (item) =>
          `<li> <strong>${ts.t(processValue(item.typeOfNonSurgicalIntervention))}</strong> (${formatDate(item.interventionDate)})</li>`
      )
      .join('') +
    `</ul>`
  );
});

const formattedProcedureList = computed(() => {
  let procedure = pdfDatasToBeSaved.proceduresTreatments;

  // Se procedure non è un array o è vuoto, restituisci '--'
  if (!Array.isArray(procedure) || procedure.length === 0) return ts.t('--');

  return (
    `<ul>` +
    procedure
      .map((item) => {
        // Tipizzare correttamente gli oggetti in procedurePeriods
        let procedurePeriodFormatted = item.procedurePeriods
          ? item.procedurePeriods
              .map(
                (period: {
                  procedureStartDate: string;
                  procedureEndDate: string;
                }) => {
                  let startFormatted = period.procedureStartDate
                    ? `${ts.t('Start Date')} ${formatDate(period.procedureStartDate)}`
                    : '';
                  let endFormatted = period.procedureEndDate
                    ? `${ts.t('End Date')} ${formatDate(period.procedureEndDate)}`
                    : '';
                  return `${startFormatted} ${endFormatted}`.trim();
                }
              )
              .join(', ')
          : '';

        // Tipizzare correttamente gli oggetti in singleDate
        let procedureSingleDateFormatted = item.singleDate
          ? item.singleDate
              .map((single: { singleStartDate: string }) => {
                return `${ts.t('at Date')} ${formatDate(single.singleStartDate)}`;
              })
              .join(', ')
          : '';

        // Aggiungi la formattazione per startDateMaxOne e endDateMaxOne
        let dateMaxFormatted = '';
        if (item.startDateMaxOne || item.endDateMaxOne) {
          let startFormatted = item.startDateMaxOne
            ? `${ts.t('Start Date')} ${formatDate(item.startDateMaxOne)}`
            : '';
          let endFormatted = item.endDateMaxOne
            ? `${ts.t('End Date')} ${formatDate(item.endDateMaxOne)}`
            : '';
          dateMaxFormatted = `${startFormatted} ${endFormatted}`.trim();
        }
        // Aggiungi la formattazione per tracheostomia, monitoraggioInvasivoGittata, sdd e surfTreat
        let tracheostomiaFormatted = item.tracheostomia
          ? `${ts.t(processValue(item.tracheostomia))}`
          : '';
        let monitoraggioInvasivoFormatted = item.monitoraggioInvasivoGittata
          ? `${ts.t(processValue(item.monitoraggioInvasivoGittata))}`
          : '';
        let sddFormatted = item.sdd ? `${ts.t(processValue(item.sdd))}` : '';
        let surfTreatFormatted = item.surfTreat
          ? `${ts.t(processValue(item.surfTreat))}`
          : '';

        // Combina le informazioni formattate
        let formattedProcedure = `
      <li>
      <strong>${ts.t(processValue(item.procedure))}</strong>
      ${procedurePeriodFormatted ? ` (${procedurePeriodFormatted})` : ''}
      ${procedureSingleDateFormatted ? ` (${procedureSingleDateFormatted})` : ''}
      ${dateMaxFormatted ? ` (${dateMaxFormatted})` : ''}${tracheostomiaFormatted ? ` (${tracheostomiaFormatted})` : ''}
      ${monitoraggioInvasivoFormatted ? ` (${monitoraggioInvasivoFormatted})` : ''}
      ${sddFormatted ? ` (${sddFormatted})` : ''}
      ${surfTreatFormatted ? ` (${surfTreatFormatted})` : ''}
      </li>`;

        return formattedProcedure;
      })
      .join('') +
    `</ul>`
  );
});

const formattedSurgicalList = computed(() => {
  let list = pdfDatasToBeSaved.surgicalList;
  let organ = pdfDatasToBeSaved.procOrganTranspList;
  let congHeart = pdfDatasToBeSaved.congHeartValSurgYesNo;

  if (!Array.isArray(list) || list.length === 0) return '--';

  return (
    `<ul>` +
    list
      .map((item) => {
        let surgInt = '';
        if (item.typeOfSurgicalIntervention && item.interventionDate) {
          let typeInt = item.typeOfSurgicalIntervention
            ? `<strong>${ts.t(processValue(item.typeOfSurgicalIntervention))}</strong>`
            : '';
          let datInt = item.interventionDate
            ? `(${formatDate(item.interventionDate)})`
            : '';
          surgInt = `${typeInt} ${datInt}`;
        }

        // Find matching orgTransId in procOrganTranspList
        let transplantation = '';
        if (Array.isArray(organ)) {
          let matchedOrgan = organ.find((org) => org.orgTransId === item.guid);
          if (matchedOrgan) {
            let transpList = Array.isArray(matchedOrgan.transplantations)
              ? matchedOrgan.transplantations
              : [matchedOrgan.transplantations];
            transplantation =
              transpList.length > 0
                ? `${transpList.map((t) => ts.t(processValue(t))).join(', ')}`
                : '';
          }
        }

        // Find matching congHeartValSurgYesNo
        let extracorpCircul = '';
        if (Array.isArray(congHeart)) {
          let matchedCongHeart = congHeart.find(
            (cong) => cong.extracorpCirculId === item.guid
          );
          if (matchedCongHeart) {
            extracorpCircul = matchedCongHeart.extracorpCircul
              ? `${ts.t('ECC (extracorporeal circulation)')} ${ts.t(processValue(matchedCongHeart.extracorpCircul))}`
              : '';
          }
        }

        // Combine results
        return `
      <li>
      ${surgInt}
      ${transplantation ? ` [${transplantation}]` : ''}
      ${extracorpCircul ? ` [${extracorpCircul}]` : ''}
      </li>`;
      })
      .join('') +
    `</ul>`
  );
});

const formattedClinicConditions = computed(() => {
  const group = pdfDatasToBeSaved.clinicCondAdm;

  if (!Array.isArray(group) || group.length === 0) {
    return ts.t('--');
  }

  // Definiamo un tipo per l'oggetto groupedConditions per mappare i gruppi
  const groupedConditions: { [key: string]: string[] } = {};

  // Raggruppiamo gli elementi in base alla parte "gruppo" (prima parte della stringa)
  group.forEach((item) => {
    const parts = item.split('.');
    const groupKey = parts.slice(0, 2).join('.'); // Raggruppiamo fino alla terza parte
    const value = item; // valore completo

    // Se il gruppo non esiste già, lo creiamo
    if (!groupedConditions[groupKey]) {
      groupedConditions[groupKey] = [];
    }
    groupedConditions[groupKey].push(ts.t(processValue(value))); // Aggiungiamo il valore tradotto al gruppo
  });

  // Mappiamo i nomi dei gruppi fissi
  const groupNames: { [key: string]: string } = {
    'admDiseasCodification.generalGroup': `${ts.t('General Group')}`,
    'admDiseasCodification.admRespiratory': `${ts.t('Respiratory Conditions')}`,
    'admDiseasCodification.admCardiovascular': `${ts.t('Cardiovascular Conditions')}`,
    'admDiseasCodification.admNeurological': `${ts.t('Neurological Conditions')}`,
    'admDiseasCodification.admNeoplasms': `${ts.t('Neoplasms')}`,
    'admDiseasCodification.admGastro': `${ts.t('Gastrointestinal Conditions')}`,
    'admDiseasCodification.admOther': `${ts.t('Other Conditions')}`,
  };

  // Creiamo l'output in formato HTML
  let result = '<ul>';

  // Per ogni gruppo, aggiungiamo una sezione fissa con i valori associati
  for (const groupKey in groupNames) {
    if (groupedConditions[groupKey] && groupedConditions[groupKey].length > 0) {
      result += `<li><strong>${ts.t(groupNames[groupKey])}</strong><ul>`; // Nome del gruppo fisso
      groupedConditions[groupKey].forEach((value: string) => {
        result += `<li>${value}</li>`; // Valori dinamici
      });
      result += '</ul></li>';
    }
  }
  result += '</ul>';
  return result;
});

const formattedIcuDiseases = computed(() => {
  const group = pdfDatasToBeSaved.icuDiseases;

  if (!Array.isArray(group) || group.length === 0) {
    return ts.t('--');
  }

  // Definiamo un tipo per l'oggetto groupedIcuDiseases per mappare i gruppi
  const groupedIcuDiseases: { [key: string]: string[] } = {};

  // Raggruppiamo gli elementi in base alla parte "gruppo" (prima parte della stringa)
  group.forEach((item) => {
    const parts = item.split('.');
    const groupKey = parts.slice(0, 2).join('.'); // Raggruppiamo fino alla terza parte
    const value = item; // valore completo

    // Se il gruppo non esiste già, lo creiamo
    if (!groupedIcuDiseases[groupKey]) {
      groupedIcuDiseases[groupKey] = [];
    }
    groupedIcuDiseases[groupKey].push(ts.t(processValue(value))); // Aggiungiamo il valore tradotto al gruppo
  });

  // Mappiamo i nomi dei gruppi fissi per le malattie ICU
  const icuGroupNames: { [key: string]: string } = {
    'complCodification.generalGroup': `${ts.t('General Group')}`,
    'complCodification.respiratory': `${ts.t('Respiratory Diseases')}`,
    'complCodification.cardiovascular': `${ts.t('Cardiovascular Diseases')}`,
    'complCodification.neurological': `${ts.t('Neurological Diseases')}`,
    'complCodification.gastrointestinalAndHepatic': `${ts.t('Gastrointestinal and Hepatic Diseases')}`,
    'complCodification.traumaComplications': `${ts.t('Trauma Complications')}`,
    'complCodification.otherDisease': `${ts.t('Other Diseases')}`,
    'complCodification.transplantComplications': `${ts.t('Transplant Complications')}`,
    'complCodification.pressureInjury': `${ts.t('Pressure Injury')}`,
  };

  // Creiamo l'output in formato HTML
  let result = '<ul>';

  // Per ogni gruppo, aggiungiamo una sezione fissa con i valori associati
  for (const groupKey in icuGroupNames) {
    if (
      groupedIcuDiseases[groupKey] &&
      groupedIcuDiseases[groupKey].length > 0
    ) {
      result += `<li><strong>${ts.t(icuGroupNames[groupKey])}</strong><ul>`; // Nome del gruppo fisso
      groupedIcuDiseases[groupKey].forEach((value: string) => {
        result += `<li>${value}</li>`; // Valori dinamici
      });
      result += '</ul></li>';
    }
  }
  result += '</ul>';
  return result;
});

const iframeVisible = ref(false);
const previewDialogRef = ref<HTMLDialogElement | null>(null);
const beforeSave = ref(false);
const showFormMessage = ref(false);
const awaitForBackend = ref(false);

const crColumns = ['Report ID', 'Send to server'];

watch(iframeVisible, () => {
  if (previewDialogRef.value) {
    iframeVisible.value
      ? previewDialogRef.value.showModal()
      : previewDialogRef.value.close();
  }
});

async function updatePdfs() {
  casePdfs = await pdfService.getPdf(caseID, centreCode);
}

async function showIframe() {
  iframeVisible.value = true;
  const html = document.getElementById('principal')!.outerHTML;
  await nextTick();
  const initString = GenerateHTMLForPrint.getInitHTML();
  const lastString = GenerateHTMLForPrint.getLastHTML();
  document
    .getElementById('iframe')!
    .setAttribute('srcdoc', initString + html + lastString);
}

async function hideIframe() {
  document.getElementById('iframe')!.setAttribute('srcdoc', '');
  beforeSave.value = false;
  iframeVisible.value = false;
}

function print() {
  const iframe = document.getElementById('iframe');
  //@ts-expect-error
  const iframeWindow = iframe!.contentWindow;

  if (iframeWindow && iframeWindow.document) {
    iframeWindow.print();
  } else {
    console.error('Iframe content not loaded.');
  }
}

async function checkBeforeSave() {
  const previewSide = document.getElementById('previewSide')!;
  if (beforeSave.value == true) {
    closeCheck();
    previewSide.style.width = '5.5vw';
    return;
  }
  const previewSideWidth = previewSide.clientWidth;
  beforeSave.value = true;
  await nextTick();
  const wrapper = document.getElementById('checkBeforeSave')!;
  const wrapperWidth = wrapper.clientWidth;
  previewSide.style.width = previewSideWidth.toString() + 'px';
  wrapper.style.opacity = '1';
  wrapper.style.left = '8.5rem';
  setTimeout(() => {
    previewSide.style.width =
      (previewSideWidth + wrapperWidth + 24).toString() + 'px';
  }, 10);
}

function closeCheck() {
  const wrapper = document.getElementById('checkBeforeSave')!;
  wrapper.style.transition =
    'left 1s, opacity 1s cubic-bezier(0, 0.4, 0.65, 1)';
  wrapper.style.opacity = '0';
  wrapper.style.left = '-16rem';
  setTimeout(() => {
    beforeSave.value = false;
  }, 1000);
}

async function displayFormMessage(
  text: string,
  isError: boolean,
  isFormOpen: boolean
) {
  showFormMessage.value = true;
  await nextTick();
  const formMessage = document.getElementById('formMessage')!;
  const formSpan = formMessage!.querySelector('span')!;
  const previewSide = document.getElementById('previewSide')!;
  formSpan.innerHTML = ts.t(text);
  if (!isError) {
    formMessage.classList?.remove('errorMessage');
    formMessage.classList.add('successMessage');
  }
  setTimeout(() => {
    formMessage.style.opacity = '1';
    formMessage.style.left = '8.5rem';
  }, 10);
  setTimeout(() => {
    formMessage.style.transition =
      'left 0.7s, opacity 1s cubic-bezier(0, 0.4, 0.65, 1)';
    formMessage.style.left = '9.5rem';
    formMessage.style.opacity = '0';
    setTimeout(() => {
      formMessage.style.left = '-16rem';
      if (!isFormOpen) {
        previewSide.style.width = '5.5vw';
      }
    }, 300);
    setTimeout(() => {
      showFormMessage.value = false;
    }, 1000);
  }, 3000);
}

function checkReportAlreadySent(isFormOpen: boolean) {
  updatePdfs();
  if (casePdfs.find((report) => report.canceled === false)) {
    awaitForBackend.value = false;
    displayFormMessage(
      'Error, there is already a report for this visit',
      true,
      isFormOpen
    );
    return false;
  }
  return true;
}

async function sendCRwithoutSign() {
  closeCheck();
  awaitForBackend.value = true;
  if (checkReportAlreadySent(false)) {
    await sendToBackend();
  }
}

async function sendToBackend(headers?: Headers) {
  const data = document.getElementById('principal')!.outerHTML;
  const pdfId = uuid();

  const casePDf: PdfData = {
    pdfUUID: pdfId,
    CaseUUID: caseID,
    centreCode,
    creationDate: new Date(),
    data,
    canceled: false,
    cancellationMessageSentDate: new Date(),
    cancellationMessageAlreadySent: false,
  };

  try {
    const response = await pdfService.savePdf(casePDf, caseID);
    const isSuccess =
      !(response instanceof Response) ||
      (response instanceof Response && response.status === 200);
    displayFormMessage(
      isSuccess ? 'The report has been saved' : 'Error saving the report',
      !isSuccess,
      headers ? true : false
    );
    if (isSuccess) {
      setTimeout(() => {
        hideIframe();
      }, 2000);
    }
  } finally {
    updatePdfs();
    awaitForBackend.value = false;
  }
}

async function cancelToBackend(pdfId: string) {
  const response = await pdfService.cancelPdf(centreCode, caseID, pdfId);
  if (
    !(response instanceof Response) ||
    (response instanceof Response && response.status === 200)
  ) {
    logger.logSuccess('The report has been cancelled', {});
  } else {
    logger.logError('Error cancelling the report', {});
  }
  updatePdfs();
  awaitForBackend.value = false;
}
</script>

<template>
  <RouterLink to="/" class="back-arrow dontprint">
    <img :src="leftArrowUrl" alt="Back arrow" />
    <span v-html="ts.t('Back')"></span>
  </RouterLink>

  <section class="report" id="principal">
    <section class="onlyprint">
      <header class="header-container">
        <div class="logo-container">
          <img
            v-if="logoPath"
            :src="logoPath"
            alt="Logo"
            class="standard-img"
            @error="handleImageError"
          />
        </div>

        <section class="patient-info">
          <h1
            v-html="
              `${ts.t('Clinical record')}: ${pdfDatasToBeSaved.ehrIdAdmission}`
            "
          ></h1>
          <p
            v-html="`${ts.t('Surname')}: ${pdfDatasToBeSaved.lastName ?? '--'}`"
          ></p>
          <p
            v-html="`${ts.t('Name')}: ${pdfDatasToBeSaved.firstName ?? '--'}`"
          ></p>
          <div class="icu-admission">
            <p
              v-html="
                `${ts.t('Admitted to ICU')}: ${pdfDatasToBeSaved.icuAdmDate ?? ''} ${ts.t('at')}: ${pdfDatasToBeSaved.icuAdmTime ?? '--'}`
              "
            ></p>
            <p
              v-html="
                `${ts.t('Discharged to ICU')}: ${pdfDatasToBeSaved.icuDisDate ?? ''} ${ts.t('at')}: ${pdfDatasToBeSaved.icuDisTime ?? '--'}`
              "
            ></p>
          </div>
        </section>
      </header>
    </section>
    <section>
      <p
        v-html="
          `<strong>${ts.t('Place of birth')}:</strong> 
            ${pdfDatasToBeSaved.placeOfBirth.town ? pdfDatasToBeSaved.placeOfBirth.town + ', ' : ''} 
            ${pdfDatasToBeSaved.placeOfBirth.province ? pdfDatasToBeSaved.placeOfBirth.province + ', ' : ''} 
            ${pdfDatasToBeSaved.placeOfBirth.country ?? ''}`
        "
      ></p>
      <p
        v-html="
          `<strong>${ts.t('Date of birth')}:</strong>  ${pdfDatasToBeSaved.birthDate ?? ''}`
        "
      ></p>
      <p
        v-html="
          `<strong>${ts.t('Address')}:</strong> 
            ${pdfDatasToBeSaved.address.street ? pdfDatasToBeSaved.address.street + ', ' : ''} 
            ${pdfDatasToBeSaved.address.postalCode ? pdfDatasToBeSaved.address.postalCode + ', ' : ''} 
            ${pdfDatasToBeSaved.address.town ? pdfDatasToBeSaved.address.town + ', ' : ''} 
            ${pdfDatasToBeSaved.address.province ? pdfDatasToBeSaved.address.province + ', ' : ''} 
            ${pdfDatasToBeSaved.address.country ?? ''}`
        "
      ></p>
    </section>
    <section>
      <h2 v-html="ts.t('Outpatient visit report')"></h2>
      <p v-html="`<strong>${ts.t('Source of admission')}:</strong>  ${pdfDatasToBeSaved.sourceOfAdmission ?? '--'}`"></p>
      <p v-html="`<strong>${ts.t('Ward type')}:</strong> ${pdfDatasToBeSaved.typeOfAdmSource}`"></p>
      <p
        v-html="
          `<strong>${ts.t('Reason for Admission')}:</strong>  ${pdfDatasToBeSaved.reasonForAdmission ?? ''}`
        "
      ></p>
      <p v-html="`<strong>${ts.t('clinic conditions on admission')}:</strong>  
        ${formattedClinicConditions}`">
      </p>

      <p
        v-html="
          `<strong>${ts.t('Comorbidities')}:</strong>  
        ${pdfDatasToBeSaved.comorbidities && pdfDatasToBeSaved.comorbidities.trim() != '' ? pdfDatasToBeSaved.comorbidities : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Allergies')}:</strong>  
        ${pdfDatasToBeSaved.allergies && pdfDatasToBeSaved.allergies.trim() != '' ? pdfDatasToBeSaved.allergies : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Family anamnesis')}:</strong>  
        ${pdfDatasToBeSaved.anamnesiFarmly && pdfDatasToBeSaved.anamnesiFarmly.trim() != '' ? pdfDatasToBeSaved.anamnesiFarmly : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Physiological Anamnesi')}:</strong>  
        ${pdfDatasToBeSaved.anamnesiPhy && pdfDatasToBeSaved.anamnesiPhy.trim() != '' ? pdfDatasToBeSaved.anamnesiPhy : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Remote pathological anamnesis')}:</strong>  
        ${pdfDatasToBeSaved.anamnesiRemota && pdfDatasToBeSaved.anamnesiRemota.trim() != '' ? pdfDatasToBeSaved.anamnesiRemota : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Recent pathological anamnesis')}:</strong>  
        ${pdfDatasToBeSaved.anamnesi && pdfDatasToBeSaved.anamnesi.trim() != '' ? pdfDatasToBeSaved.anamnesi : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Pharmacological anamnesis')}:</strong>  
        ${pdfDatasToBeSaved.anamnesiFarm && pdfDatasToBeSaved.anamnesiFarm.trim() != '' ? pdfDatasToBeSaved.anamnesiFarm : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Complications occurring during the hospital stay')}:</strong>  
        ${formattedIcuDiseases}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Antibiotic therapy during hospital stay')}:</strong>  
        ${pdfDatasToBeSaved.antibiotici && pdfDatasToBeSaved.antibiotici.trim() != '' ? pdfDatasToBeSaved.antibiotici : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Procedures and/or treatments')}:</strong>  ${formattedProcedureList}`
        "
      ></p>
      <p
        v-html="
          `<strong>${ts.t('Surgical interventions')}:</strong>  ${formattedSurgicalList}`
        "
      ></p>
      <p
        v-html="
          `<strong>${ts.t('Non surgical interventions')}:</strong>  ${formattedNonSurgicalList}`
        "
      ></p>
      <p
        v-html="
          `<strong>${ts.t('Clinical course')}:</strong>  
        ${pdfDatasToBeSaved.decorsoClinico && pdfDatasToBeSaved.decorsoClinico.trim() !== '' ? pdfDatasToBeSaved.decorsoClinico : '--'}`
        "
      ></p>

      <p
        v-html="
          `<strong>${ts.t('Current therapy at discharge')}:</strong>  
        ${pdfDatasToBeSaved.terapia && pdfDatasToBeSaved.terapia.trim() !== '' ? pdfDatasToBeSaved.terapia : '--'}`
        "
      ></p>

      <footer>
        <div class="footer-content">
          <p>{{ ts.t('Date') }}:</p>
          <p>{{ ts.t('doctor signature') }}:</p>
        </div>

        <div class="footer-logo-container">
          <img v-if="footerPath" :src="footerPath" alt="Footer" @error="handleFooterImageError">
        </div>
      </footer>

    </section>
  </section>

  <button
    class="open-print-preview-btn dontprint"
    v-if="!iframeVisible"
    @click="showIframe()"
    v-html="ts.t('Print preview')"
  ></button>

  <dialog ref="previewDialogRef" class="pdf-preview dialog">
    <div class="pdf-preview-content">
      <iframe id="iframe"></iframe>
      <section id="previewSide">
        <ion-icon
          id="closePreview"
          name="close-outline"
          @click="hideIframe()"
        ></ion-icon>
        <div class="utils-buttons">
          <button @click="print()">
            <ion-icon name="print-outline"></ion-icon>
          </button>
          <button @click="checkBeforeSave()">
            <ion-icon name="save-outline"></ion-icon>
          </button>
          <div id="checkBeforeSave" v-if="beforeSave">
            <span v-html="`${ts.t('Do you really want to save')}?`"></span>
            <button @click="sendCRwithoutSign()" v-html="ts.t('Yes')"></button>
            <button @click="checkBeforeSave()" v-html="ts.t('No')"></button>
          </div>
          <div id="formMessage" class="errorMessage" v-if="showFormMessage">
            <span></span>
          </div>
          <div v-if="awaitForBackend" id="spinner"></div>
        </div>
      </section>
    </div>
  </dialog>

  <section id="visitReletedReports" v-if="casePdfs.length > 0">
    <h2 v-html="ts.t('Reports related to the visit')"></h2>
    <table>
      <tbody>
        <tr>
          <th v-for="column in crColumns" v-html="ts.t(column)"></th>
          <th></th>
        </tr>
        <tr v-for="casePdf in casePdfs">
          <td v-html="casePdf.pdfUUID"></td>
          <td>
            <a
              :href="'data:application/pdf;base64,' + casePdf.data"
              target="_blank"
              v-html="ts.t('See report')"
            ></a>
          </td>
          <td>
            <button
              @click="cancelToBackend(casePdf.pdfUUID)"
              v-html="ts.t('Cancel')"
            ></button>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<style scoped>
h2 {
  font-size: 3ch;
}

h3 {
  color: navy;
  font-size: 2.5ch;
}

.pagedjs_area {
  margin-top: 10%;
}

.pagedjs_pagebox > .pagedjs_area {
  margin-top: 10% !important;
}

.report {
  border-radius: var(--rounded-box, 1rem);
  margin: 2rem;
  --tw-bg-opacity: 1;
  background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  padding: 1.5rem;
  --tw-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  --tw-shadow-colored:
    0 20px 25px -5px var(--tw-shadow-color),
    0 8px 10px -6px var(--tw-shadow-color);
  box-shadow:
    var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
    var(--tw-shadow);
}

.report,
#visitReletedReports {
  margin: 1rem;
}

.back-arrow {
  display: flex;
  align-items: center;
  max-width: fit-content;
  margin: 0.5rem 0 0 0.5rem;
  gap: 0.5rem;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 0.25rem;
}

.report h1 {
  margin: 1rem 0 2rem 0;
}

.report h2,
#visitReletedReports h2 {
  margin: 1rem 0;
}

.report p {
  max-width: fit-content;
}

.open-print-preview-btn {
  margin-left: 1rem;
  max-width: fit-content;
}
</style>

<style>
iframe {
  height: 86vh;
  width: 42.5vw;
}

.pdf-preview {
  top: 5vh;
  padding: 1.5rem;
  overflow: hidden;
}

.pdf-preview-content {
  display: flex;
  align-items: end;
}

.pdf-preview .utils-buttons {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.pdf-preview .utils-buttons > button {
  height: 3rem;
  min-width: 0;
  padding: 0.85rem;
}

#previewSide {
  height: 86vh;
  width: 5.5vw;
  display: flex;
  flex-direction: column;
  justify-content: end;
  position: relative;
  margin-left: 1rem;
  transition: width 1s;
}

#previewSide > ion-icon[name='close-outline'] {
  position: absolute;
  top: 0;
  right: 0;
  color: #ff5065;
  font-size: 32px;
}

#previewSide > ion-icon[name='close-outline']:hover {
  background-color: #ff5065;
  border-radius: 0.25rem;
  color: #fff;
}

#previewSide button {
  display: flex;
  align-items: center;
  justify-content: center;
}

#previewSide button ion-icon {
  font-size: 24px;
}

#signForm {
  position: absolute;
  width: 27.85rem;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid #000;
  opacity: 1;
  bottom: -18rem;
  opacity: 0;
  transition:
    bottom 1s,
    opacity 1s;
}

#signForm .emptyInput {
  border: 2px solid #ff5065;
}

#signForm #formHeader {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font-size: 22px;
}

#signForm #formHeader h3 {
  margin: 1rem 0;
}

#signForm #formHeader ion-icon {
  position: absolute;
  top: 0;
  right: 0;
}

#signForm #formHeader ion-icon:hover {
  background-color: #000080;
  color: #fff;
}

#signForm input {
  height: 3rem;
  border: 1px solid #000;
  background-color: rgba(255, 255, 255, 0.07);
  padding: 0 10px;
  margin: 0.5rem 0;
  font-size: 15px;
}

#signForm #sendBtn {
  margin-top: 0.5rem;
}

#visitReletedReports {
  margin-top: 2rem;
}

#visitReletedReports table {
  box-shadow: var(--shadow-small, 0 0.125rem 1.25rem 0 rgb(0 0 0 / 20%));
}

#checkBeforeSave,
#formMessage {
  display: flex;
  position: absolute;
  left: -16rem;
  align-items: center;
  gap: 8px;
  color: #fff;
  padding: 0.6rem;
  opacity: 0;
  transition:
    left 1s,
    opacity 1s cubic-bezier(0.7, 0, 1, 0.75);
}

#formMessage {
  border-radius: 0.25rem;
}

#checkBeforeSave {
  border-radius: 0.5rem;
  background-color: #000080;
  padding: 0.6rem 0.8rem;
}

#checkBeforeSave::before {
  border-right-color: #000080 !important;
}

#checkBeforeSave::before,
#formMessage::before {
  content: ' ';
  position: absolute;
  left: -1.5rem;
  border: 0.8rem solid transparent;
}

.errorMessage {
  background-color: #c42525;
}

.errorMessage::before {
  border-right-color: #c42525 !important;
}

.successMessage {
  background-color: #23dcbb;
}

.successMessage::before {
  border-right-color: #23dcbb !important;
}

#checkBeforeSave button {
  width: 4rem;
  min-width: 0;
  border-radius: 0.25rem;
}

#spinner {
  position: absolute;
  right: 0;
  border: 8px solid #d4d4d4;
  border-radius: 50%;
  border-top: 8px solid rgb(88, 88, 88);
  width: 2rem;
  height: 2rem;
  -webkit-animation: spin 1s linear infinite;
  /* Safari */
  animation: spin 1s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
